// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { Rectangle } from '@shared/commons/Geometry';
import { DrawPointer, DrawPointerTypeEnum } from '../Utils/DrawPointer';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { Pen, SolidBrush } from '@shared/commons/Graphics';
import { TradingToolViewBase } from '../Tools/TradingTools/TradingToolViewBase';
export class TradingCentralLinesRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this.dataObj = null;
        this.textWidthCached = {};
        this.resistanceLocalizedText = '';
        this.supportLocalizedText = '';
        this.pivotLocalizedText = '';
        this.ThemeChanged();
        this.Localize();
        this.SetClassName('TradingCentralLinesRenderer');
    }
    Draw(gr, window, windowsContainer, advParams) {
        if (!this.Visible)
            return;
        const clipRect = window.ClientRectangle;
        if (!advParams || !clipRect)
            return;
        const data = this.dataObj;
        if (!data)
            return;
        const resistanceArr = data.resistanceValues; // draw resistances
        if (resistanceArr) {
            for (let i = 0; i < resistanceArr.length; i++) {
                this.DrawFullSet(gr, window, resistanceArr[i], this.resistancePen, this.resistanceBrush, this.resistanceLocalizedText + (i + 1), advParams);
            }
        }
        const supportArr = data.supportValues; // draw supports
        if (supportArr) {
            for (let i = 0; i < supportArr.length; i++) {
                this.DrawFullSet(gr, window, supportArr[i], this.supportPen, this.supportBrush, this.supportLocalizedText + (i + 1), advParams);
            }
        }
        this.DrawFullSet(gr, window, data.pivotValue, this.pivotPen, this.pivotBrush, this.pivotLocalizedText, advParams); // draw pivot
    }
    DrawFullSet(gr, window, valueForY, pen, brush, textName, advParams) {
        if (isNaN(valueForY))
            return;
        const clientRect = window.ClientRectangle;
        const Y = window.PointsConverter.GetScreenY(valueForY);
        if (Y < 0 || Y > clientRect.Height)
            return;
        gr.DrawLine(pen, clientRect.X + 1, Y, clientRect.X + clientRect.Width, Y);
        this.DrawPriceRectangle(gr, clientRect, Y, valueForY, brush, advParams);
        this.DrawNameRectangle(gr, clientRect, Y, textName);
    }
    DrawPriceRectangle(gr, clientRect, Y, price, brush, advParams) {
        if (!advParams.DrawPointers)
            return;
        advParams.DrawPointers.push(new DrawPointer(DrawPointerTypeEnum.Indicator, price, brush, price));
    }
    DrawNameRectangle(gr, clientRect, Y, textName) {
        if (!this.textWidthCached[textName])
            this.textWidthCached[textName] = gr.GetTextWidth(textName, this.font);
        const plateWidth = this.textWidthCached[textName] + TradingCentralLinesRenderer.NAME_PLATE_TEXT_MARGIN;
        const plateHeight = TradingCentralLinesRenderer.NAME_PLATE_HEIGHT;
        const offset = TradingCentralLinesRenderer.NAME_PLATE_OFFSET;
        const x = clientRect.X + clientRect.Width - plateWidth - offset;
        const y = Y - plateHeight - offset;
        gr.RenderButton(this.namePlateBrush, this.namePlateBorderPen, new Rectangle(x, y, plateWidth, plateHeight));
        gr.DrawString(textName, this.font, this.namePlateTextBrush, x + plateWidth / 2, y + plateHeight / 2, 'center', 'middle');
    }
    setLinesValues(dataObj) {
        if (!dataObj)
            return;
        this.dataObj = dataObj;
    }
    FindMinMax(minMaxResult, window) {
        const data = this.dataObj;
        if (!data)
            return false;
        minMaxResult.tMin = Number.MAX_VALUE;
        minMaxResult.tMax = -Number.MAX_VALUE;
        const resistanceValues = data.resistanceValues || [];
        const supportValues = data.supportValues || [];
        const pivotValue = data.pivotValue ? [data.pivotValue] : [];
        const allValues = resistanceValues.concat(supportValues, pivotValue);
        for (let i = 0; i < allValues.length; i++) {
            const value = allValues[i];
            if (!isNaN(value)) {
                minMaxResult.tMin = Math.min(minMaxResult.tMin, value);
                minMaxResult.tMax = Math.max(minMaxResult.tMax, value);
            }
        }
        return true;
    }
    Localize() {
        const prefix = 'panel.tradingCentral.chart.';
        this.resistanceLocalizedText = Resources.getResource(prefix + 'resistance');
        this.supportLocalizedText = Resources.getResource(prefix + 'support');
        this.pivotLocalizedText = Resources.getResource(prefix + 'pivot');
    }
    ThemeChanged() {
        super.ThemeChanged();
        const theme = ThemeManager.colors;
        this.namePlateBrush = TradingToolViewBase.grayBackgroundBrush;
        this.namePlateBorderPen = TradingToolViewBase.grayBackgroundPen;
        this.namePlateTextBrush = new SolidBrush(theme.chartTradingCentralLineNameForeground);
        const lineW = 1;
        this.pivotPen = new Pen(theme.TC_pivotLineColor, lineW);
        this.resistancePen = new Pen(theme.TC_resistanceLineColor, lineW);
        this.supportPen = new Pen(theme.TC_supportLineColor, lineW);
        this.pivotBrush = new SolidBrush(theme.TC_pivotLineColor);
        this.resistanceBrush = new SolidBrush(theme.TC_resistanceLineColor);
        this.supportBrush = new SolidBrush(theme.TC_supportLineColor);
        this.font = ThemeManager.Fonts.Font_10F_regular;
    }
}
TradingCentralLinesRenderer.NAME_PLATE_HEIGHT = 16;
TradingCentralLinesRenderer.NAME_PLATE_TEXT_MARGIN = 10;
TradingCentralLinesRenderer.NAME_PLATE_OFFSET = 2;
